import { Card, CardContent, Tooltip, TooltipContent, TooltipTrigger } from '@monksflow/monks-ui';
import { EM_DASH, formatMetric, type MetricFormat } from '@/shared/utils/metric-format';
import { percentChange } from '../../lib/metrics';

export interface MetricTileProps {
  label: string;
  value: number | null;
  format: MetricFormat;
  currency?: string | null;
  previousValue?: number | null;
  /**
   * Why this metric is absent, when it is. Rendered as the em dash's accessible label,
   * so "the platform never told us" is readable rather than implied by a dash.
   */
  unreportedReason?: string;
  /**
   * Figures to show in place of a single value, for a metric that is known but cannot be
   * reduced to one number: spend across several currencies has no total without an FX
   * rate, yet every part of it is known. Showing the parts beats a dash whose meaning
   * only appears on hover.
   */
  valueLines?: ReadonlyArray<string>;
}

/**
 * One headline metric.
 *
 * A null value renders as an em dash with its reason attached, never as 0 — and the delta
 * is omitted entirely rather than shown as 0% when there is nothing to compare against.
 *
 * Assumes an ambient `TooltipProvider` from an ancestor (the app mounts none at its root;
 * every other tooltip-bearing component here relies on one from a nearby parent instead of
 * self-providing). A row of these tiles should sit under one shared `TooltipProvider`, not
 * one per tile.
 */
export function MetricTile({
  label,
  value,
  format,
  currency = null,
  previousValue = null,
  unreportedReason,
  valueLines,
}: MetricTileProps) {
  const delta = percentChange(value, previousValue);
  const formatted = formatMetric(value, format, currency);

  return (
    <Card compact data-testid="metric-tile">
      <CardContent compact className="flex flex-col gap-1">
        <span className="text-sm text-muted-foreground">{label}</span>

        {value === null && valueLines && valueLines.length > 0 ? (
          <div className="flex flex-col gap-0.5" data-testid="metric-tile-lines">
            {valueLines.map((line) => (
              <span key={line} className="text-lg font-semibold">
                {line}
              </span>
            ))}
          </div>
        ) : value === null && unreportedReason ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-2xl font-semibold" aria-label={unreportedReason}>
                {EM_DASH}
              </span>
            </TooltipTrigger>
            <TooltipContent>{unreportedReason}</TooltipContent>
          </Tooltip>
        ) : (
          <span className="text-2xl font-semibold">{formatted}</span>
        )}

        {delta !== null && (
          <span
            data-testid="metric-tile-delta"
            className={`text-sm ${delta >= 0 ? 'text-chart-positive' : 'text-chart-negative'}`}
          >
            {delta >= 0 ? '+' : ''}
            {(delta * 100).toFixed(2)}%
          </span>
        )}
      </CardContent>
    </Card>
  );
}
